import { useEffect, useId, useMemo, useRef, useState } from "react";

import { coverLines, coverTargets, foodCover, portionShares, type CoverTargets } from "../domain/cover";
import { againEntry, customFoodByCode, foodTable } from "../domain/customFoods";
import { potHolds } from "../domain/dish";
import { ESTIMATE_LEGEND, isEstimatedFood, kcalText, macroText } from "../domain/foodView";
import { MEALS, MEAL_LABEL, OIL_KCAL_PER_TSP, entryFor, householdUnits, portionGrams } from "../domain/intake";
import { lastPortions, looksLikeMeal, type LastPortion } from "../domain/mealText";
import { dailyTargets } from "../domain/nutrition";
import type { FoodEntry, FoodItem, MealSlot } from "../domain/types";
import {
  SEARCH_ALL_BELOW,
  allFoodsReady,
  foodByCode,
  loadAllFoods,
  loadFoods,
  mergeWider,
  searchAllFoods,
  searchFoods,
} from "../lib/foodDb";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import { ActionBar } from "./ActionBar";
import { IconButton } from "./Button";
import { Chip, ChipRow } from "./Chip";
import { CoverBars } from "./CoverBars";
import { CustomFoodSheet } from "./CustomFoodSheet";
import { EstimateLegend, Kcal } from "./Estimate";
import { I } from "./icons";
import { NavRow } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";
import { Tag } from "./Tag";
import { TypedMealRows, useTypedMeal } from "./TypedMeal";

/** Which meal a tap at this hour most likely means. Saves a choice four times a day. */
export function mealNow(d = new Date()): MealSlot {
  const h = d.getHours();
  if (h < 11) return "breakfast";
  if (h < 16) return "lunch";
  if (h < 21) return "dinner";
  return "snack";
}

const OILS = [0, 1, 2, 3].map((n) => ({ value: n, label: n === 0 ? "None" : `${n} tsp` }));

/** "My rajma" → "your rajma": the Add button speaks to the person about their own dish. */
function yourName(name: string): string {
  return /^my\s/i.test(name) ? `your ${name.slice(3)}` : name;
}

/**
 * The Add-food sheet: search, the meal, then the chosen food opens in place for how much.
 *
 * Choosing the food and choosing how much of it are different questions, so the portion controls
 * appear only under the row that was tapped; the meal sits above the list because it is decided
 * once for everything added in a sitting. Shared by the food log and the planner: a planned item
 * is the same entry, not yet eaten. Every entry is built by `entryFor`, which copies every
 * nutrient in at this moment (invariant 18).
 *
 * The list is the generated table plus the user's own foods (ADR-032), theirs tagged "yours".
 * Whatever is typed can become one: the last row offers to add it, and a food saved from there
 * opens here chosen, so the portion is the next thing asked.
 *
 * When the table the app opens with has fewer than a handful of matches, a "Search all foods" row
 * fetches the rest of USDA's tables once (ADR-037). From then on a short list is topped up from it
 * without asking again; its rows come after the Indian table's and carry a "USDA" tag, because a
 * US survey's "chicken curry" is a different recipe from the one at home and the person choosing
 * should know which they are logging.
 *
 * With `onPickMany`, a line that reads as a meal — "2 roti, 1 katori dal, chai" — is read into rows
 * to confirm instead of searched (`TypedMeal`, ADR-048). A chosen food opens at the portion last
 * logged of it. A dish of the user's own asks no oil question: the oil is in the dish. In
 * `ingredient` mode — what went into a dish — there is no meal, no oil and no cover: an ingredient
 * is weighed into a pot, not eaten on its own.
 */
export function FoodPicker({
  onClose,
  onPick,
  onPickMany,
  title = "Add food",
  meal: initialMeal,
  action = "Add",
  quick,
  mode = "log",
  initialQuery = "",
}: {
  onClose: () => void;
  onPick: (e: FoodEntry) => void;
  /** several at once, from a typed meal; without it a typed line is searched as words */
  onPickMany?: (es: FoodEntry[]) => void;
  title?: string;
  /** the meal to preselect; defaults to the one the clock suggests */
  meal?: MealSlot;
  action?: string;
  /** frequent foods, offered as one-tap chips while the search box is empty */
  quick?: FoodEntry[];
  mode?: "log" | "ingredient";
  /** the words to open on — a typed-meal part being picked by hand */
  initialQuery?: string;
}) {
  const ingredient = mode === "ingredient";
  const [q, setQ] = useState(initialQuery);
  const [meal, setMeal] = useState<MealSlot>(initialMeal ?? mealNow());
  const [chosen, setChosen] = useState<FoodItem | null>(null);
  const [db, setDb] = useState<FoodItem[] | null>(null);
  const [adding, setAdding] = useState(false);
  // A typed-meal part being picked by hand: its row key and the words to search.
  const [changing, setChanging] = useState<{ key: string; words: string } | null>(null);
  // "idle" until asked; the wider table then stays in memory for the session (lib/foodDb.ts).
  const [wide, setWide] = useState<"idle" | "loading" | "ready" | "failed">(
    allFoodsReady() ? "ready" : "idle"
  );
  // A food just saved from the Add-a-food sheet, chosen once it shows up in the table.
  const [pending, setPending] = useState<string | null>(null);
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();
  const search = useRef<HTMLInputElement>(null);
  const id = useId();
  const table = useMemo(() => (db ? foodTable(S, db) : null), [S, db]);
  const last = useMemo(() => lastPortions(S), [S]);
  const count = table ? table.length : null;
  const typedMode = !!onPickMany && !ingredient && looksLikeMeal(q);
  const typedMeal = useTypedMeal(typedMode ? q : "", table, last, meal);
  // Re-run once the table arrives, so a query typed while it loaded is not left empty.
  const first = useMemo(() => (table && !typedMode ? searchFoods(q, 25, table) : []), [q, table, typedMode]);
  const short = !typedMode && q.trim().length >= 2 && first.length < SEARCH_ALL_BELOW;
  const wider = useMemo(() => (short && wide === "ready" ? searchAllFoods(q, 25) : []), [q, short, wide]);
  const widerIds = useMemo(() => new Set(wider.map((f) => f.i)), [wider]);
  const results = useMemo(() => (wider.length ? mergeWider(first, wider) : first), [first, wider]);
  const searchAll = () => {
    setWide("loading");
    loadAllFoods()
      .then(() => setWide("ready"))
      .catch(() => setWide("failed"));
  };
  const lookup = (code: string) => customFoodByCode(S, code) ?? foodByCode(code);
  // Today's targets and budget: what each food's words are worked out against (ADR-040). With no
  // body numbers there is no budget, and a food gets no words rather than words about nobody.
  const { cover, budget } = useMemo(() => {
    const T = dailyTargets(S, undefined, undefined, device);
    return { cover: coverTargets(T), budget: T.kcal };
  }, [S]);

  useEffect(() => {
    loadFoods()
      .then(setDb)
      .catch(() => setDb(null));
    // The sheet focuses its pane when it opens; the search box is where typing should start.
    const t = window.setTimeout(() => search.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  // The chosen food stays on screen while the query is edited, rather than vanishing mid-portion.
  const list = chosen && !results.some((f) => f.i === chosen.i) ? [chosen, ...results] : results;
  const words = useMemo(
    () => (ingredient ? list.map(() => null) : coverLines(list.map((f) => foodCover(f, cover, budget)))),
    [list, cover, budget, ingredient]
  );
  const portion = usePortion(chosen, meal);
  const choose = (f: FoodItem | null) => {
    portion.reset(f, f ? (last.get(f.i) ?? null) : null);
    setChosen(f);
  };

  useEffect(() => {
    if (!pending || !table) return;
    const f = table.find((x) => x.i === pending);
    if (!f) return;
    setPending(null);
    portion.reset(f, null);
    setChosen(f);
    // `portion` is rebuilt every render; only a newly saved food should move the choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, table]);

  const again = (prev: FoodEntry) => {
    const e = againEntry(prev, prev.code ? lookup(prev.code) : null, { meal });
    if (e) onPick(e);
  };

  const typed = q.trim();
  const allRow =
    short && wide !== "ready" ? (
      <div role="listitem" className="k-food-result">
        <NavRow
          icon={I.dl}
          tone="brand"
          title="Search all foods"
          subtitle={
            wide === "loading"
              ? "Fetching 13,000 more foods…"
              : wide === "failed"
                ? "Could not fetch them. Try again when online"
                : "13,000 more, fetched once and kept for offline"
          }
          onClick={wide === "loading" ? undefined : searchAll}
        />
      </div>
    ) : null;
  const addRow = ingredient ? null : (
    <div role="listitem" className="k-food-result">
      <NavRow
        icon={I.plus}
        tone="brand"
        title={`Add “${typed}” as a food`}
        subtitle="From a label, or a dish you cook"
        onClick={() => setAdding(true)}
      />
    </div>
  );

  const dishChosen = chosen?.dishG !== undefined;
  const addLabel = (() => {
    if (!chosen || !portion.preview) return chosen ? `${action} ${chosen.n}` : action;
    if (dishChosen && chosen.src === "c") return `${action} ${portion.qtyWords} of ${yourName(chosen.n)}`;
    return `${action} ${portion.qtyWords} · ${kcalText(portion.preview.kcal, isEstimatedFood(chosen))}`;
  })();

  const footer = typedMode ? (
    <ActionBar
      inSheet
      secondary={{ label: "Cancel", onClick: onClose }}
      primary={{
        label: typedMeal.entries.length
          ? `${action} ${typedMeal.entries.length} food${typedMeal.entries.length === 1 ? "" : "s"} · ${typedMeal.entries
              .reduce((a, e) => a + e.kcal, 0)
              .toLocaleString()} kcal`
          : `${action} foods`,
        icon: I.check,
        disabled: !typedMeal.entries.length,
        reason: typedMeal.entries.length ? undefined : "Nothing found to add yet",
        onClick: () => {
          if (!typedMeal.entries.length || !onPickMany) return;
          // Stamped in the order typed, so the meal lists them as the line read — a part picked
          // by hand was built earlier and would otherwise sort first.
          const now = Date.now();
          onPickMany(typedMeal.entries.map((e, i) => ({ ...e, at: now + i })));
        },
      }}
    />
  ) : chosen ? (
    <ActionBar
      inSheet
      secondary={{ label: "Cancel", onClick: () => choose(null) }}
      primary={{
        label: addLabel,
        icon: I.check,
        disabled: !portion.preview,
        reason: portion.preview ? undefined : "Enter how much first",
        onClick: () => {
          if (portion.preview) onPick(portion.preview);
        },
      }}
    />
  ) : undefined;

  return (
    <Sheet title={title} onClose={onClose} footer={footer}>
      <label className="k-food-label" htmlFor={`${id}-q`}>
        {onPickMany && !ingredient
          ? count
            ? `Type a meal, or search ${count.toLocaleString()} foods`
            : "Type a meal, or search foods"
          : count
            ? `Search ${count.toLocaleString()} foods`
            : "Search foods"}
      </label>
      <input
        ref={search}
        id={`${id}-q`}
        className="k-food-input"
        type="search"
        value={q}
        placeholder={onPickMany && !ingredient ? "2 roti, 1 katori dal, chai" : "dal, roti, chai, paneer…"}
        onChange={(e) => setQ(e.target.value)}
      />
      {typedMode ? <p className="k-food-note">Recent foods come first, each at the portion you last logged.</p> : null}

      {ingredient ? null : (
        <Segmented
          ariaLabel="Meal"
          value={meal}
          onChange={setMeal}
          options={MEALS.map((m) => ({ value: m, label: MEAL_LABEL[m] }))}
        />
      )}

      {!ingredient && quick?.length && q.trim().length < 2 ? (
        <ChipRow ariaLabel="Log again">
          {quick.map((f) => (
            <Chip
              key={f.id}
              icon={I.plus}
              disabled={!f.code || !count || (!lookup(f.code) && !f.code.startsWith("cf:"))}
              onClick={() => again(f)}
            >
              {f.name}, {f.qty} {f.unit}
            </Chip>
          ))}
        </ChipRow>
      ) : null}

      {typedMode ? (
        <TypedMealRows meal={typedMeal} onChange={(key, words) => setChanging({ key, words })} />
      ) : q.trim().length < 2 ? (
        <p className="k-food-note">
          {onPickMany && !ingredient
            ? "Type two letters or more, or a whole meal with commas. Spelling does not have to be exact."
            : "Type two letters or more. Spelling does not have to be exact."}
        </p>
      ) : list.length ? (
        <>
          <div className="k-rows" role="list">
            {list.map((f, n) => {
              const open = chosen?.i === f.i;
              const per = f.g / 100;
              const dish = f.dishG !== undefined;
              return (
                <div key={f.i} role="listitem" className="k-food-result">
                  <button
                    type="button"
                    className="k-row k-row--link"
                    aria-expanded={open}
                    onClick={() => choose(open ? null : f)}
                  >
                    <span className="k-row__txt">
                      <span className="k-row__title">
                        {f.n}
                        {f.src === "c" ? <Tag tone="yours" /> : null}
                        {widerIds.has(f.i) ? <Tag tone="neutral">USDA</Tag> : null}
                      </span>
                      <span className="k-row__sub">
                        {dish
                          ? `${f.u}, ${f.g} g · your dish, ${Math.round(f.k)} kcal per 100 g`
                          : `${f.u}, ${f.g} g · ${macroText({ p: f.p * per, c: f.c * per, f: f.f * per })}`}
                      </span>
                      {words[n] ? <span className="k-row__sub">{words[n]}</span> : null}
                    </span>
                    <span className="k-row__value">
                      <Kcal kcal={f.k * per} estimated={isEstimatedFood(f)} />
                    </span>
                  </button>
                  {open ? (
                    <PortionPanel
                      item={f}
                      portion={portion}
                      oil={!ingredient && !dish}
                      cover={ingredient || budget === null ? null : cover}
                    />
                  ) : null}
                </div>
              );
            })}
            {allRow}
            {addRow}
          </div>
          {/* One footnote under the group, not a line per row: the words are per calorie, and a
              katori of palak is still a katori. (SNIPPETS §29) */}
          {words.some((w) => w !== null) ? (
            <p className="k-food-foot">
              <span>Per calorie, against your own targets — a portion still matters</span>
              <Tag tone="est" />
            </p>
          ) : null}
          {list.some(isEstimatedFood) ? <EstimateLegend>{ESTIMATE_LEGEND}</EstimateLegend> : null}
        </>
      ) : (
        <>
          <p className="k-food-note">
            Nothing matched &ldquo;{typed}&rdquo;. Try a shorter word or the plainer name
            {ingredient ? "." : " — or add it as your own food, from its label or as a dish you cook."}
          </p>
          <div className="k-rows" role="list">
            {allRow}
            {addRow}
          </div>
        </>
      )}
      {ingredient ? null : (
        <CustomFoodSheet
          open={adding}
          prefillName={typed}
          onClose={() => setAdding(false)}
          onSaved={setPending}
        />
      )}
      {changing ? (
        <FoodPicker
          title={`Pick a food for “${changing.words}”`}
          action="Use"
          meal={meal}
          initialQuery={changing.words}
          onClose={() => setChanging(null)}
          onPick={(e) => {
            typedMeal.pick(changing.key, e);
            setChanging(null);
          }}
        />
      ) : null}
    </Sheet>
  );
}

interface Portion {
  qty: string;
  setQty: (s: string) => void;
  unit: string;
  setUnit: (u: string) => void;
  oil: number;
  setOil: (n: number) => void;
  /** the entry as it would be written, or null while the amount is empty (invariant 11) */
  preview: FoodEntry | null;
  qtyWords: string;
  /** a newly chosen food, at the portion last logged of it when there is one (ADR-048) */
  reset: (item: FoodItem | null, last: LastPortion | null) => void;
}

/** The units a row's portion can be counted in: its own, a spoon the other way, and grams. */
function unitsOf(item: FoodItem): string[] {
  return [...householdUnits(item).map((h) => h.unit), "g"];
}

/** The portion being built for `item`. The caller resets it whenever a different food is chosen. */
function usePortion(item: FoodItem | null, meal: MealSlot): Portion {
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("g");
  const [oil, setOil] = useState(0);

  const n = Number(qty);
  const safe = Number.isFinite(n) ? n : 0;
  const built = item ? entryFor(item, { qty: safe, unit, meal, oilTsp: item.dishG !== undefined ? 0 : oil }) : null;
  return {
    qty,
    setQty,
    unit,
    setUnit,
    oil,
    setOil,
    // An emptied box is "no portion yet", not a zero-calorie meal (invariant 11).
    preview: built && built.grams > 0 ? built : null,
    qtyWords: `${safe.toLocaleString()} ${unit}`,
    reset: (next, last) => {
      // The last portion only where its unit is still one this row can be counted in — a row
      // regenerated with a different unit would otherwise read "2 katori" of a food in pieces.
      const keep = next && last && last.qty > 0 && unitsOf(next).includes(last.unit) ? last : null;
      setQty(keep ? String(keep.qty) : "1");
      setUnit(keep ? keep.unit : (next?.u ?? "g"));
      setOil(0);
    },
  };
}

function PortionPanel({
  item,
  portion,
  oil: askOil,
  cover,
}: {
  item: FoodItem;
  portion: Portion;
  /** false for a dish (the oil is in it) and for an ingredient (oil is its own ingredient) */
  oil: boolean;
  /** today's targets, or null without the body numbers */
  cover: CoverTargets | null;
}) {
  const id = useId();
  const { qty, setQty, unit, setUnit, oil, setOil, preview } = portion;
  const grams = unit === "g";
  const step = grams ? 10 : 0.5;
  const n = Number(qty);
  const cur = Number.isFinite(n) ? n : 0;
  const nudge = (d: number) => setQty(String(Math.max(0, Math.round((cur + d) * 10) / 10)));
  const units = unitsOf(item);
  const holds = item.dishG !== undefined ? potHolds(item.dishG, item.g) : null;

  // Switching units keeps the same amount of food, just counted differently.
  const switchUnit = (u: string) => {
    if (u === unit) return;
    const g = portionGrams(item, cur, unit);
    if (u === "g") setQty(String(Math.round(g)));
    else {
      const per = portionGrams(item, 1, u);
      setQty(String(Math.max(0.5, Math.round((g / per) * 2) / 2)));
    }
    setUnit(u);
  };

  return (
    <div className="k-food-portion">
      <span className="k-food-label" id={`${id}-how`}>
        How much
      </span>
      <div className="k-food-stepper" role="group" aria-labelledby={`${id}-how`}>
        <IconButton icon={I.minus} label={`Less, by ${step} ${unit}`} onClick={() => nudge(-step)} />
        <input
          className="k-food-input k-food-qty"
          type="number"
          inputMode="decimal"
          step={step}
          min="0"
          aria-label={`How much, in ${grams ? "grams" : unit}`}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
        />
        <IconButton icon={I.plus} label={`More, by ${step} ${unit}`} onClick={() => nudge(step)} />
        <span className="k-food-note">
          {grams ? "g" : unit}
          {!grams && preview ? ` · ${preview.grams} g` : ""}
        </span>
      </div>
      <Segmented
        ariaLabel="Count in"
        value={unit}
        onChange={switchUnit}
        options={units.map((u) => ({ value: u, label: u === "g" ? "grams" : u }))}
      />

      {askOil ? (
        <>
          {/* The research's single most important control: a tablespoon of ghee is ~120 kcal and
              leaves no trace on the plate, so it has to be asked for rather than inferred. */}
          <span className="k-food-label">Oil or ghee added</span>
          <Segmented ariaLabel="Oil or ghee added" value={oil} onChange={setOil} options={OILS} />
          <p className="k-food-note">Each teaspoon adds {OIL_KCAL_PER_TSP} kcal. A tablespoon is three.</p>
        </>
      ) : item.dishG !== undefined ? (
        <p className="k-food-note">
          The oil and ghee are in the dish already.
          {holds !== null ? ` Your pot holds ${holds.toLocaleString()} ${item.u}.` : ""}
        </p>
      ) : null}

      <p className="k-food-note k-food-fig">
        {preview
          ? `${preview.grams} g · P ${preview.p} · C ${preview.c} · F ${preview.f} g`
          : "Enter how much to see the figures."}
      </p>
      {/* The portion against the day, beside the per-calorie words: palak is several times its
          share per calorie, and this katori is what it actually brings. */}
      {cover && preview ? (
        <CoverBars
          caption="This portion’s share of today’s needs · the full bar is the need"
          rows={portionShares(preview, cover).map((r) => ({
            key: r.key,
            label: r.label,
            unit: r.unit,
            share: r.share,
            amount: r.value,
            target: r.target,
          }))}
        />
      ) : null}
    </div>
  );
}
